import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

export interface ThreatData {
    id: string;
    value: number;
}

interface GlobalThreatMapProps {
    data?: ThreatData[];
    isLoading?: boolean;
    
}

const GlobalThreatMap: React.FC<GlobalThreatMapProps> = ({ data = [], isLoading }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; country: string; count: number } | null>(null);

    // Store parsed geography
    const geoDataRef = useRef<any>(null);
    const projectionRef = useRef<d3.GeoProjection | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    // 1. Fetch World Map Topology
    useEffect(() => {
        d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json').then((world: any) => {
            const countries = topojson.feature(world, world.objects.countries);
            geoDataRef.current = countries;
            setIsMapLoaded(true);
        });
    }, []);

    // 2. Handle Responsive Resizing
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            if (!entries.length) return;
            const { width, height } = entries[0].contentRect;
            setDimensions({ width, height });
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // 3. Main Render Engine (Upgraded to Canvas Choropleth)
    useEffect(() => {
        if (!canvasRef.current || dimensions.width === 0 || !isMapLoaded || !geoDataRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // High-DPI Display Scaling
        const dpr = window.devicePixelRatio || 1;
        canvas.width = dimensions.width * dpr;
        canvas.height = dimensions.height * dpr;
        canvas.style.width = `${dimensions.width}px`;
        canvas.style.height = `${dimensions.height}px`;

        // Reset transform before scaling to prevent compounding
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);

        // Clear canvas with pure black
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, dimensions.width, dimensions.height);

        // Setup Projection
        const projection = d3.geoEquirectangular()
            .fitSize([dimensions.width, dimensions.height], geoDataRef.current)
            .translate([dimensions.width / 2, dimensions.height / 2 + 20]);

        projectionRef.current = projection;
        const pathGenerator = d3.geoPath(projection, ctx);

        // Create a fast lookup map for the threat data O(1)
        const threatMap = new Map(data.map(d => [d.id.toLowerCase(), d.value]));

        // Find maximum value to normalize our color scale
        const maxVal = data.length > 0 ? Math.max(...data.map(d => d.value)) : 1;

        // Create a continuous color gradient mapping
        const colorScale = d3.scaleLinear<string>()
            .domain([0, maxVal * 0.15, maxVal * 0.6, maxVal])
            .range(["#050505", "#0f3a20", "#50A970", "#00FFFF"]); // Black -> Dark Green -> Brand Green -> Neon Cyan

        // --- DRAW THE UNIFIED MAP ---
        geoDataRef.current.features.forEach((feature: any) => {
            const countryName = feature.properties.name.toLowerCase();
            const threatValue = threatMap.get(countryName) || 0;

            ctx.beginPath();
            pathGenerator(feature);

            if (threatValue > 0) {
                ctx.fillStyle = colorScale(threatValue);

                // Add a faint neon glow only to the most heavily targeted countries
                if (threatValue > maxVal * 0.6) {
                    ctx.shadowColor = '#00FFFF';
                    ctx.shadowBlur = 15;
                } else {
                    ctx.shadowBlur = 0;
                }
            } else {
                // Empty countries get a very dark slate color
                ctx.fillStyle = '#080A0E';
                ctx.shadowBlur = 0;
            }

            ctx.fill();

            // Draw crisp, faint borders over the fills
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
            ctx.lineWidth = 0.5;
            ctx.stroke();
        });

    }, [dimensions, data, isMapLoaded, isLoading]);

    // 4. Handle Hover Interaction (Unchanged - Already optimized!)
    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !projectionRef.current || !geoDataRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const invertedCoords = projectionRef.current.invert?.([mouseX, mouseY]);

        if (invertedCoords) {
            const hoveredFeature = geoDataRef.current.features.find((f: any) =>
                d3.geoContains(f, invertedCoords)
            );

            if (hoveredFeature) {
                const countryName = hoveredFeature.properties.name;
                const threatInfo = data.find(d => d.id.toLowerCase() === countryName.toLowerCase());
                const count = threatInfo ? threatInfo.value : 0;

                setTooltip({
                    visible: true,
                    x: mouseX,
                    y: mouseY,
                    country: countryName,
                    count: count
                });
            } else {
                setTooltip(null);
            }
        }
    }, [data]);

    return (
        <div ref={containerRef} className="w-full h-full relative bg-black">
            <canvas
                ref={canvasRef}
                className="absolute inset-0 z-0 cursor-crosshair"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setTooltip(null)}
            />

            {/* Tooltip */}
            {tooltip && tooltip.visible && (
                <div
                    className="absolute z-30 bg-[#0A0B0E]/90 backdrop-blur border border-[#1A1D24] p-3 rounded shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full transition-all duration-75"
                    style={{ left: tooltip.x, top: tooltip.y - 15 }}
                >
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">{tooltip.country}</div>
                    <div className="flex items-end gap-2">
                        <span className="text-xl font-bold text-white leading-none">{tooltip.count.toLocaleString()}</span>
                        <span className="text-[10px] text-[#50A970] uppercase tracking-wide">Threats</span>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {(!isMapLoaded || isLoading) && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 backdrop-blur-sm text-[#00E5FF] font-mono text-sm animate-pulse pointer-events-none">
                    Syncing global topology...
                </div>
            )}
        </div>
    );
};

export default GlobalThreatMap;